<script setup lang="ts">
import type { SwiperContainer } from 'swiper/element'
import type { Image } from '~/types'

export interface TimelineItem {
  image?: Image
  title?: string
  description?: string
  youtubeEmbedUrl?: string
}

interface Props {
  anchor?: string
  title?: string
  items?: TimelineItem[]
}

const props = defineProps<Props>()

const selectedVideo = ref<TimelineItem | undefined>()

const selectedVideoUrl = computed<string | undefined>(() => selectedVideo.value?.youtubeEmbedUrl)

if (props.items && props.items.length > 0) {
  selectedVideo.value = props.items[0]
}

const { embedUrl } = useYouTubeVideo(selectedVideoUrl)

const containerRef = ref<SwiperContainer | null>(null)

useSwiper(containerRef, {
  spaceBetween: 10,
  slidesPerView: 1.3,
  direction: 'horizontal',
  freeMode: true,
  mousewheel: true,
  watchSlidesProgress: true,
  grabCursor: true,
  scrollbar: {
    draggable: true,
  },
  keyboard: {
    enabled: true,
    onlyInViewport: false,
  },
  a11y: {
    enabled: false,
  },
  breakpoints: {
    767: {
      slidesPerView: 2.3,
      direction: 'horizontal',
    },
    992: {
      slidesPerView: 2,
      direction: 'vertical',
    },
    1200: {
      slidesPerView: 3,
      direction: 'vertical',
    },
  },
  injectStyles: [
    `
    :host .swiper {
      width: 100%;
      height: 100%;
      padding-right: 30px;
      padding-bottom: 0;
    }
    :host .swiper-scrollbar {
      right: 10px;
      left: unset;
      width: 8px;
    }
    :host .swiper-scrollbar-drag {
      cursor: pointer;
      transition-property: color, background-color, background;
      transiton-duration: 300ms;
    }
    :host .swiper-scrollbar-drag:hover,
    :host .swiper-scrollbar-drag:focus {
      background-color: black;
    }
    @media only screen and (max-width: 1279px) {
      :host .swiper {
        padding-right: 0;
        padding-bottom: 30px;
      }

      :host .swiper-scrollbar {
        left: -10px;
        width: 100%;
      }
    }
    `,
  ],
})
</script>

<template>
  <section
    :id="anchor"
    class="timeline"
  >
    <div class="wrapper">
      <CommonSectionTitle
        v-if="title"
        :title="title"
      />
      <ClientOnly>
        <div class="timeline__content">
          <div class="timeline__video">
            <div class="timeline__video-item">
              <iframe
                v-if="embedUrl"
                width="560"
                height="315"
                :src="embedUrl"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
              />
            </div>
          </div>
          <div class="timeline__thumbnails">
            <swiper-container
              ref="containerRef"
              :init="false"
              class="timeline__thumbnails-slider"
            >
              <swiper-slide
                v-for="(item, index) in items"
                :key="`timeline-item-${index}`"
                tag="div"
              >
                <ContentBlockVideosTimelineElement
                  :item="item"
                  @click.prevent="selectedVideo = item"
                />
              </swiper-slide>
            </swiper-container>
          </div>
        </div>
        <template #fallback>
          <span>Loading...</span>
        </template>
      </ClientOnly>
    </div>
  </section>
</template>

<style lang="scss">
.timeline {
  @apply my-[30px];
  @apply md:my-[40px];
  @apply lg:my-[60px];

  .wrapper {
    @apply md:max-w-[740px] md:px-0;
    @apply lg:max-w-[900px];
    @apply xl:max-w-[1340px];
  }

  &__content {
    @apply flex flex-col;
    @apply lg:flex-row lg:h-[400px];
    @apply xl:h-[600px];
  }

  &__video {
    @apply w-full h-[260px] shrink-0;
    @apply md:h-[400px];
    @apply lg:w-[70%] lg:h-full;

    &-item {
      @apply w-full h-full;
    }

    iframe {
      @apply w-full h-full;
    }
  }

  &__thumbnails {
    @apply w-full p-[15px] pr-0 bg-light-background h-[230px];
    @apply md:p-[30px] md:pr-0 md:h-[270px];
    @apply lg:grow lg:w-auto lg:h-full;

    &-slider {
      @apply w-full h-full pb-[30px];
      @apply lg:pr-[30px] lg:pb-0;
    }

    &-item {
      @apply w-full h-full relative z-[1] bg-dark-background flex flex-col justify-end overflow-hidden;
    }

    &-image {
      @apply absolute left-0 top-0 w-full h-full -z-[1] object-cover transition-transform duration-300;
    }

    &-item:hover &-image {
      @apply scale-105;
    }

    &-meta {
      @apply px-[15px] py-[20px] bg-gradient-to-t from-black to-black/0;

      h3 {
        @apply text-[16px] font-medium text-light-text mb-[5px] leading-[1.2];
        @apply xl:text-[20px];
      }

      p {
        @apply text-[14px] text-light-text leading-[1.2];
      }
    }
  }
}
</style>
